import * as PropTypes from 'prop-types';
import { isObject } from '@philips-internal/oswald-ui-utils';
/**
 * Wraps a simple type checker (e.g.: any, array, bool, element, func, list, node, number, object, string, symbol).
 * Adds info as provided.
 *
 * @param typeChecker the base type checker to build upon
 * @param info the info to be merged with the property info
 */
function wrapType(typeChecker, info) {
    const oldInfo = extractInfo(typeChecker);
    const tcWithInfo = createChainableTypeChecker(typeChecker);
    tcWithInfo.__info__ = { ...oldInfo, ...info };
    return tcWithInfo;
}
/**
 * Wraps a composite type checker (e.g.: arrayOf, exact, instanceOf, objectOf, oneOf, oneOfType and shape).
 * Captures the arguments and stores them in the args property.
 * Adds info as provided.
 *
 * @param baseCompositeTypeChecker the base type checker to build upon
 * @param info the info to be merged with the property info
 * @param recursive whether to extract info recursively or not
 */
function wrapCompositeType(baseCompositeTypeChecker, info, recursive = false) {
    return (argument, ...rest) => {
        const args = compositeArguments(argument, recursive);
        return wrapType(baseCompositeTypeChecker(argument, ...rest), { ...info, args });
    };
}
/**
 * Processes the composite argument and extract its info.
 *
 * @param argument the argument that will further refine the composite type checker
 * @param recursive whether to extract info recursively or not
 */
function compositeArguments(argument, recursive = false) {
    if (!recursive) {
        return argument;
    }
    if (isTypeChecker(argument)) {
        return extractInfo(argument);
    }
    if (Array.isArray(argument)) {
        return extractInfoCollection(argument);
    }
    if (isObject(argument)) {
        return extractInfoCollection(argument);
    }
    return argument;
}
/**
 * Adds chainable methods to the custom type checker.
 *
 * @param typeChecker the custom type checker
 */
function createChainableTypeChecker(typeChecker) {
    const chainableTypeChecker = typeChecker.bind(null);
    function moot(name, value) {
        return () => wrapType(chainableTypeChecker, { [name]: value });
    }
    function setter(name, defaultValue) {
        return value => wrapType(chainableTypeChecker, { [name]: value ?? defaultValue });
    }
    chainableTypeChecker.deprecated = moot('deprecated', true);
    chainableTypeChecker.group = setter('group');
    chainableTypeChecker.maxLength = setter('maxLength', 40);
    chainableTypeChecker.notEnumerable = moot('enumerable', false);
    chainableTypeChecker.placeholder = setter('placeholder');
    chainableTypeChecker.readOnly = moot('readOnly', true);
    chainableTypeChecker.type = setter('type');
    chainableTypeChecker.unique = moot('unique', true);
    return chainableTypeChecker;
}
/**
 * Extracts the info of a custom type checker.
 *
 * @param typeChecker the custom type checker
 */
function extractInfo(typeChecker) {
    const { __info__: info } = typeChecker;
    return info ?? {
        type: getTypeCheckerName(typeChecker),
    };
}
/**
 * Extracts info of any custom type checker found.
 *
 * @param typeCheckers the custom type checkers
 */
function extractInfoCollection(typeCheckers) {
    return Object.entries(typeCheckers)
        .map(([name, value]) => {
        if (!isTypeChecker(value)) {
            return value;
        }
        const base = { name };
        return {
            ...base,
            ...extractInfo(value),
        };
    });
}
/**
 * Checks whether the info represents an enumerable custom type checker or not.
 *
 * @param info the custom type checker info
 */
function isEnumerable(info) {
    return info.enumerable !== false;
}
/**
 * Checks whether the argument is a custom type checker or not.
 *
 * @param value the value to be checked
 */
function isTypeChecker(value) {
    return typeof value === 'function';
}
/**
 * Discovers the type checker name.
 * If not found within standard prop-types, returns custom.
 *
 * @param typeChecker the type checker
 */
function getTypeCheckerName(typeChecker) {
    const entries = Object.entries(PropTypes);
    const fallbackName = 'custom';
    for (const [name, propType] of entries) {
        if (propType === typeChecker) {
            return name ?? fallbackName;
        }
    }
    return fallbackName;
}
export { extractInfoCollection, isEnumerable, wrapCompositeType, wrapType, };
//# sourceMappingURL=util.js.map